import { useCallback, useRef, useState } from 'react';
import type { PersistedSession } from '../lib/session-store';

/**
 * One open file in the editor.
 *
 * Active tab: `snapshot` is null — the live state lives in the editor's
 * useImageEditorState / Fabric canvas / useCanvasHistory hooks.
 *
 * Inactive tabs: `snapshot` is a serialized {@link PersistedSession} captured
 * at the moment the user switched away. Switching back restores it.
 */
export interface TabDoc {
  id: string;
  /** Display name (e.g. "photo.jpg"). Mirrors the editor's `state.fileName`. */
  fileName: string;
  /** Cloud file id, if the doc came from / is saved to Fias storage. */
  fileId: string | null;
  /**
   * File System Access API handle, present when the user opened a local
   * file in a browser that supports `window.showOpenFilePicker`. Save can
   * use this to overwrite the original file in place; without it (Firefox
   * / Safari, or files chosen via the `<input type="file">` fallback),
   * Save degrades to a download.
   */
  fileHandle: FileSystemFileHandle | null;
  /** Whether the doc has unsaved edits. Drives the "*" indicator on the tab. */
  isDirty: boolean;
  /** Serialized state for inactive tabs; null while the tab is active. */
  snapshot: PersistedSession | null;
}

let tabIdSeq = 0;
function nextTabId(): string {
  tabIdSeq += 1;
  return `tab_${Date.now()}_${tabIdSeq}`;
}

interface UseDocumentTabsResult {
  tabs: TabDoc[];
  activeTabId: string | null;
  /**
   * Append a new tab and make it active. By default the newly active tab
   * starts with a null snapshot — caller is expected to populate the editor
   * with whatever content this tab represents (e.g. by calling loadImage).
   *
   * Pass `snapshot` to seed an inactive-style snapshot up-front; the
   * tab-switch effect will then apply it on the next render. Used by the
   * IndexedDB session-restore flow on mount to avoid racing the effect's
   * "freshly-created tab → resetEditorToEmpty" branch.
   */
  addTab: (initial: {
    fileName: string;
    fileId?: string | null;
    fileHandle?: FileSystemFileHandle | null;
    snapshot?: PersistedSession | null;
  }) => string;
  /**
   * Remove a tab. If the removed tab was active, the previous tab becomes
   * active (or null if no tabs remain). Returns the new active id (or null).
   */
  closeTab: (id: string) => string | null;
  /** Make `id` the active tab. No-op if it's already active. */
  setActiveTabId: (id: string) => void;
  /**
   * Write a snapshot to the named tab. Used during a switch to capture the
   * outgoing tab's state before the new one's snapshot is restored.
   */
  setTabSnapshot: (id: string, snapshot: PersistedSession | null) => void;
  /** Patch `fileName` / `fileId` / `isDirty` on the active tab. */
  updateActiveTabMetadata: (patch: {
    fileName?: string;
    fileId?: string | null;
    isDirty?: boolean;
  }) => void;
}

export function useDocumentTabs(): UseDocumentTabsResult {
  const [tabs, setTabs] = useState<TabDoc[]>([]);
  const [activeTabId, setActiveTabIdState] = useState<string | null>(null);
  // Mirror state into refs so callbacks stay referentially stable but still
  // see fresh values.
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const activeIdRef = useRef(activeTabId);
  activeIdRef.current = activeTabId;

  const addTab: UseDocumentTabsResult['addTab'] = useCallback((initial) => {
    const id = nextTabId();
    const tab: TabDoc = {
      id,
      fileName: initial.fileName,
      fileId: initial.fileId ?? null,
      fileHandle: initial.fileHandle ?? null,
      isDirty: false,
      snapshot: initial.snapshot ?? null,
    };
    setTabs((prev) => [...prev, tab]);
    setActiveTabIdState(id);
    return id;
  }, []);

  const closeTab: UseDocumentTabsResult['closeTab'] = useCallback((id) => {
    const prevTabs = tabsRef.current;
    const idx = prevTabs.findIndex((t) => t.id === id);
    if (idx === -1) return activeIdRef.current;
    const remaining = prevTabs.filter((t) => t.id !== id);
    let nextActive: string | null = activeIdRef.current;
    if (activeIdRef.current === id) {
      // Pick the neighbor: prefer the tab to the left, else the one to the
      // right, else nothing.
      const neighborIdx = idx > 0 ? idx - 1 : 0;
      nextActive = remaining[neighborIdx]?.id ?? null;
      setActiveTabIdState(nextActive);
    }
    setTabs(remaining);
    return nextActive;
  }, []);

  const setActiveTabId: UseDocumentTabsResult['setActiveTabId'] = useCallback((id) => {
    if (activeIdRef.current === id) return;
    setActiveTabIdState(id);
  }, []);

  const setTabSnapshot: UseDocumentTabsResult['setTabSnapshot'] = useCallback((id, snapshot) => {
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, snapshot } : t)));
  }, []);

  const updateActiveTabMetadata: UseDocumentTabsResult['updateActiveTabMetadata'] = useCallback(
    (patch) => {
      const id = activeIdRef.current;
      if (!id) return;
      setTabs((prev) => {
        let changed = false;
        const next = prev.map((t) => {
          if (t.id !== id) return t;
          // Skip if every field would be unchanged — keeps both the tab
          // object AND the tabs array referentially stable to avoid
          // triggering downstream re-renders for no-op metadata updates.
          if (
            (patch.fileName === undefined || patch.fileName === t.fileName) &&
            (patch.fileId === undefined || patch.fileId === t.fileId) &&
            (patch.isDirty === undefined || patch.isDirty === t.isDirty)
          ) {
            return t;
          }
          changed = true;
          return {
            ...t,
            fileName: patch.fileName ?? t.fileName,
            fileId: patch.fileId === undefined ? t.fileId : patch.fileId,
            isDirty: patch.isDirty ?? t.isDirty,
          };
        });
        return changed ? next : prev;
      });
    },
    [],
  );

  return {
    tabs,
    activeTabId,
    addTab,
    closeTab,
    setActiveTabId,
    setTabSnapshot,
    updateActiveTabMetadata,
  };
}
